import {
  BusinessCreateRequestOptionType,
  BusinessEntityType,
  BusinessGetResponseType,
} from "@sellar-io/api-v2/types";
import { CountryType, sanitizeEmailArray } from "@sellar-io/common";
import {
  type FormAsyncValidateOrFn,
  type FormValidateOrFn,
  type ReactFormExtendedApi,
} from "@tanstack/react-form";
import {
  generateBuyerFieldValues,
  BuyerFieldValues,
} from "../buyer/BuyerFormFragment.shared";
import {
  generateSupplierFieldValues,
  SupplierFieldValues,
} from "../supplier/SupplierFormFragment.shared";
import {
  BusinessAddressFieldValues,
  generateBusinessAddressFieldValues,
} from "./BusinessAddressFormValues";

export type BusinessFieldValues = {
  name: string;
  addresses: BusinessAddressFieldValues;
  logoUrl: string;
  awrsNumber: string;
  hasNoAwrsNumber: boolean;
  vatNumber: string;
  hasNoVatNumber: boolean;
  entityType: BusinessEntityType | "";
  companyNumber: string;
  phoneNumber: string;
  websiteUrl: string;
  instagram: string;
  twitter: string;
  email: string;
  invoiceEmails: string;
  isInvoiceEmailAddressSame: boolean;
  registeredCompanyName: string;
  deliveryInstructions: string;
};

export const generateBusinessFieldValues = (
  business?: BusinessGetResponseType,
): BusinessFieldValues => {
  const existingInvoiceEmail = sanitizeEmailArray(
    business?.invoiceEmails ?? [],
  )[0];
  const existingEmail = sanitizeEmailArray([business?.email])[0];

  const isInvoiceEmailAddressSame =
    business?.invoiceEmails.length === 1 &&
    Boolean(existingInvoiceEmail) &&
    Boolean(existingEmail)
      ? existingInvoiceEmail === existingEmail
      : true;

  return {
    registeredCompanyName: business?.registeredCompanyName ?? "",
    name: business?.name ?? "",
    email: business?.email ?? "",
    logoUrl: business?.logoUrl ?? "",
    addresses: generateBusinessAddressFieldValues(business),
    awrsNumber: business?.awrsNumber ?? "",
    entityType: business?.entityType ?? "",
    vatNumber: business?.vatNumber ?? "",
    companyNumber: business?.companyNumber ?? "",
    phoneNumber: normalizePhoneNumberFieldValue(business?.phoneNumber),
    websiteUrl: business?.websiteUrl ?? "",
    instagram: business?.instagram ?? "",
    twitter: business?.twitter ?? "",
    invoiceEmails: business?.invoiceEmails.join(",") ?? "",
    isInvoiceEmailAddressSame,
    hasNoAwrsNumber: false,
    hasNoVatNumber: false,
    deliveryInstructions: business?.deliveryInstructions ?? "",
  };
};

const normalizePhoneNumberFieldValue = (phoneNumber?: string | null) => {
  if (!phoneNumber) return "";

  const trimmedPhoneNumber = phoneNumber.trim();
  if (!trimmedPhoneNumber || trimmedPhoneNumber.startsWith("+")) {
    return trimmedPhoneNumber;
  }

  if (trimmedPhoneNumber.startsWith("0")) {
    return `+44${trimmedPhoneNumber.slice(1)}`;
  }

  return trimmedPhoneNumber;
};

export interface BusinessFormFieldValues extends BusinessFieldValues {
  Supplier?: SupplierFieldValues | null;
  Retailer?: BuyerFieldValues | null;
  teamEmails?: string[];
}

export const generateSellarAddressFromGoogleResults = (
  searchGooglePlacesResult: google.maps.places.PlaceResult,
) => {
  const findValue = (addressType: string) => {
    return searchGooglePlacesResult.address_components?.find(
      ({ types }: { types: string[] }) => types.includes(addressType),
    )?.short_name;
  };
  const name =
    findValue("street_number") ??
    findValue("neighborhood") ??
    findValue("establishment");
  const route = findValue("route");
  const firstLine = [name, route].filter(Boolean).join(" ");
  return {
    name: firstLine,
    street: "",
    city: findValue("postal_town") ?? "",
    region: findValue("region") ?? "",
    postalCode: findValue("postal_code") ?? "",
    country: (findValue("country") ?? "GB") as CountryType,
  };
};

export const generateBusinessFormFieldValues = (
  business?: BusinessGetResponseType,
): BusinessFormFieldValues => {
  if (business?.Supplier) {
    return {
      ...generateBusinessFieldValues(business),
      Supplier: generateSupplierFieldValues(business.Supplier),
      Retailer: null,
    };
  }

  if (business?.Retailer) {
    return {
      ...generateBusinessFieldValues(business),
      Retailer: generateBuyerFieldValues(business.Retailer),
      Supplier: null,
    };
  }

  return generateBusinessFieldValues();
};

export type BusinessFormType = "supplier" | "buyer";
export type BusinessFormApi = ReactFormExtendedApi<
  BusinessFormFieldValues,
  undefined | FormValidateOrFn<BusinessFormFieldValues>,
  undefined | FormValidateOrFn<BusinessFormFieldValues>,
  undefined | FormAsyncValidateOrFn<BusinessFormFieldValues>,
  undefined | FormValidateOrFn<BusinessFormFieldValues>,
  undefined | FormAsyncValidateOrFn<BusinessFormFieldValues>,
  undefined | FormValidateOrFn<BusinessFormFieldValues>,
  undefined | FormAsyncValidateOrFn<BusinessFormFieldValues>,
  undefined | FormValidateOrFn<BusinessFormFieldValues>,
  undefined | FormAsyncValidateOrFn<BusinessFormFieldValues>,
  undefined | FormAsyncValidateOrFn<BusinessFormFieldValues>,
  unknown
>;

export const generateBusinessRequest = (
  business: BusinessFormFieldValues,
  type: BusinessFormType | "retailer",
): BusinessCreateRequestOptionType => {
  const {
    addresses,
    Supplier,
    Retailer,
    teamEmails: _teamEmails,
    ...rest
  } = business;
  const invoiceEmails = sanitizeEmailArray(rest.invoiceEmails.split(","));

  if (type === "supplier") {
    if (!Supplier) throw new Error("Supplier should be defined");
    if (typeof Supplier.vatPercentage !== "number") {
      throw new Error("Supplier VAT percentage should be defined");
    }

    return {
      ...rest,
      type: "supplier",
      invoiceEmails,
      Supplier: {
        ...Supplier,
        transactionFee: Supplier.transactionFee,
        vatPercentage: Supplier.vatPercentage,
      },
      InvoiceAddress: addresses.isInvoiceAddressSame
        ? addresses.DeliveryAddress
        : addresses.InvoiceAddress,
      CollectionAddress: addresses.isCollectionAddressSame
        ? addresses.DeliveryAddress
        : addresses.CollectionAddress,
      DeliveryAddress: addresses.DeliveryAddress,
      entityType: rest.entityType || null,
    };
  }

  return {
    ...rest,
    type: "retailer",
    invoiceEmails,
    InvoiceAddress: addresses.isInvoiceAddressSame
      ? addresses.DeliveryAddress
      : addresses.InvoiceAddress,
    CollectionAddress: addresses.isCollectionAddressSame
      ? addresses.DeliveryAddress
      : addresses.CollectionAddress,
    DeliveryAddress: addresses.DeliveryAddress,
    entityType: rest.entityType || null,
    Retailer: {
      ...Retailer,
      ownershipType: Retailer?.ownershipType || null,
    },
  };
};
